import type { ComponentType } from "react";
import { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { IconButton } from "@similarweb/ui-components";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import { MANAGEMENT_ROUTE } from "app/pages/sales-intelligence/constants/routes";
import Steps from "app/pages/sales-intelligence/common-components/steps/Steps/Steps";
import {
    SL_BACK_BTN_TEXT,
    SL_REVIEW_AND_APPROVE_STEP,
    SL_UPLOAD_LIST_STEP,
} from "../../translations/suppressionListKeys";
import { SuppressionListActions } from "../../store/action-creators";
import { CreateSuppressionListStep } from "../types";
import { StyledManagementContainer, StyledManagementWrapper } from "../style";
import { LoadListView } from "./components/LoadListView";
import { AssignListNameView } from "./components/AssignListNameView";
import { StyledBackBtnWrapper, StyledUploadListPageHeader } from "./style";
import withSecondaryBarSet from "app/pages/sales-intelligence/hoc/withSecondaryBarSet";
import { ConfirmQuitModal } from "./components/ConfirmQuitModal";
import { selectCreateListStep } from "../../store/selectors/suppressionListSelectors";
import { translate } from "app/filters/i18nFilter";
import { useMount } from "app/custom-hooks/useMount";

type StepViewComponent = ComponentType<{ onCancel?(): void }>;

const createSuppressionListViewMap: Record<CreateSuppressionListStep, StepViewComponent> = {
    [CreateSuppressionListStep.UPLOAD_FILE]: LoadListView,
    [CreateSuppressionListStep.ASSIGN_NAME]: AssignListNameView,
};

const LoadListContainer = () => {
    const dispatch = useDispatch();
    const currentStep: CreateSuppressionListStep = useSelector(selectCreateListStep);
    const StepView = createSuppressionListViewMap[currentStep];
    const [isConfirmQuitModalVisible, setIsConfirmQuitModalVisible] = useState(false);

    useMount(() => () => {
        dispatch(SuppressionListActions.resetParsedList());
        dispatch(SuppressionListActions.setCreateListStep(CreateSuppressionListStep.UPLOAD_FILE));
    });

    const handleClickOnBack = () => setIsConfirmQuitModalVisible(true);
    const handleCloseConfirmQuitModal = () => setIsConfirmQuitModalVisible(false);
    const handleConfirmQuit = () => proNavigator.go(MANAGEMENT_ROUTE);

    return (
        <StyledManagementContainer>
            <StyledManagementWrapper isSupressionList>
                <StyledUploadListPageHeader>
                    <StyledBackBtnWrapper>
                        <IconButton
                            type="flat"
                            iconName="arrow-left"
                            onClick={handleClickOnBack}
                        >
                            {translate(SL_BACK_BTN_TEXT)}
                        </IconButton>
                    </StyledBackBtnWrapper>
                    <Steps
                        steps={[translate(SL_UPLOAD_LIST_STEP), translate(SL_REVIEW_AND_APPROVE_STEP)]}
                        currentStep={currentStep}
                    />
                </StyledUploadListPageHeader>

                <StepView onCancel={handleClickOnBack} />

                <ConfirmQuitModal
                    isOpen={isConfirmQuitModalVisible}
                    onClose={handleCloseConfirmQuitModal}
                    onConfirm={handleConfirmQuit}
                />
            </StyledManagementWrapper>
        </StyledManagementContainer>
    );
};

export default withSecondaryBarSet("SalesIntelligenceManagement")(LoadListContainer);
